import { useInView } from "react-intersection-observer";
import EmptyMessage from "../../common/components/EmptyMessage";
import FetchMore from "../../common/components/FetchMore";
import Loader from "../../common/components/Loader";
import { twMerge } from "tailwind-merge";
import { ChevronUpDownIcon } from "@heroicons/react/24/outline";
import { SortDirection } from "global";
import { ArrowLeftIcon, ArrowRightIcon } from "@heroicons/react/24/outline";

interface WrapperProps {
  children: React.ReactNode;
  className?: string;
}

function Wrapper({ children, className }: WrapperProps) {
  return (
    <div
      className={twMerge(
        "flex max-w-full flex-col h-full",
        className
      )}
    >
      {children}
    </div>
  );
}

interface HeaderProps<K extends string> {
  tabs: {
    label: string;
    sortKey?: K;
    className?: string;
  }[];
  sort?: {
    key: K;
    direction: SortDirection;
    onSort: (key?: K, direction?: SortDirection) => void;
  };
}

function Header<K extends string>({ tabs, sort }: HeaderProps<K>) {
  return (
    <div className="flex w-full items-center justify-between rounded-t-lg border border-slate-700 bg-slate-700 py-2">
      {tabs.map((tab, index) => {
        const isSortable = !!tab.sortKey;
        const isSorted = sort?.key === tab.sortKey;
        const sortDirection = isSorted ? sort?.direction : null;
        const onSort = sort?.onSort;

        const onSortIconPress = () => {
          if (!onSort || !tab?.sortKey) {
            return;
          }

          if (sortDirection === "ASC") {
            onSort(tab.sortKey, "DESC");
          } else {
            onSort(tab.sortKey, "ASC");
          }
        };

        return (
          <p
            onClick={onSortIconPress}
            className={twMerge(
              "flex w-full items-center justify-center whitespace-nowrap px-2 text-sm font-bold text-white",
              isSortable &&
                "cursor-pointer transition-colors duration-300 hover:text-zinc-300",
              tab?.className
            )}
            key={tab.label + index}
          >
            {tab.label}
            {isSortable && (
              <ChevronUpDownIcon className="h-4 w-4 cursor-pointer text-zinc-300" />
            )}
          </p>
        );
      })}
    </div>
  );
}

interface RowProps {
  children: React.ReactNode;
  isSelected?: boolean;
  onClick?: () => void;
}

function Row({ children, isSelected = false, onClick }: RowProps) {
  const isReadyToBeClicked = !!onClick && !isSelected;

  return (
    <div
      className={twMerge(
        "flex w-full justify-between border-b border-zinc-300 py-3",
        isReadyToBeClicked &&
          "cursor-pointer transition-colors duration-300 hover:bg-zinc-100",
        isSelected && "bg-zinc-100 shadow-inner"
      )}
      onClick={onClick}
    >
      {children}
    </div>
  );
}

interface RowManageButtonsProps {
  onUpdate?: () => void;
  onDelete?: () => void;
  className?: string;
}

function RowManageButtons({
  onDelete,
  onUpdate,
  className,
}: RowManageButtonsProps) {
  if (!onUpdate && !onDelete) {
    return (
      <Column className={`${className} text-zinc-500`}>(관리 불가)</Column>
    );
  }

  return (
    <Column className={className}>
      {!!onUpdate && (
        <button
          onClick={(event) => {
            event.stopPropagation();

            if (onUpdate) {
              onUpdate();
            }
          }}
          className="cursor-pointer whitespace-nowrap font-semibold hover:text-blue-500"
        >
          수정
        </button>
      )}
      {!!onDelete && !!onUpdate && <span className="text-zinc-500">/</span>}
      {!!onDelete && (
        <button
          onClick={(event) => {
            event.stopPropagation();

            if (onDelete) {
              onDelete();
            }
          }}
          className="cursor-pointer whitespace-nowrap font-semibold hover:text-red-500"
        >
          삭제
        </button>
      )}
    </Column>
  );
}

interface ColumnProps {
  children: React.ReactNode;
  fallback?: React.ReactNode;
  className?: string;
}

function Column({ children, fallback = "-", className = "" }: ColumnProps) {
  return (
    <div
      className={twMerge(
        "flex w-full max-w-full items-center justify-center gap-1 whitespace-nowrap border-e px-2 text-sm",
        className
      )}
    >
      {children || fallback}
    </div>
  );
}

interface BodyProps {
  children?: React.ReactNode;
  isLoading?: boolean;
  isEmpty?: boolean;
  emptyMessage?: string;
  infiniteScroll?: {
    isFetchingMore: boolean;
    hasNextPage: boolean;
    onViewChange: (inView: boolean) => void;
  };
}

function Body({
  children,
  isLoading,
  isEmpty,
  emptyMessage,
  infiniteScroll,
}: BodyProps) {
  const [fetchMoreRef] = useInView({
    threshold: 0.3,
    onChange: infiniteScroll?.onViewChange,
  });

  const isInfiniteScroll = !!infiniteScroll;

  if (isLoading) {
    return (
      <div className="h-full w-full border">
        <Loader />
      </div>
    );
  }

  if (isEmpty) {
    return (
      <div className="flex h-full w-full items-center justify-center bg-white border border-t-0 border-zinc-300">
        <EmptyMessage message={emptyMessage} />
      </div>
    );
  }

  return (
    <div className="flex-1 w-full border border-t-0 border-zinc-300 bg-white overflow-y-auto overflow-x-auto">
      {children}
      {isInfiniteScroll && (
        <>
          {infiniteScroll.isFetchingMore && (
            <div className="p-[20px]">
              <Loader />
            </div>
          )}
          {infiniteScroll.hasNextPage && (
            <FetchMore fetchMoreRef={fetchMoreRef} />
          )}
        </>
      )}
    </div>
  );
}

interface PaginationProps {
  page: number;
  total: number;
  onPageChange: (page: number) => void;
  isMovedSingleStepOnly?: boolean;
}

function Pagination({
  page,
  total,
  isMovedSingleStepOnly = false,
  onPageChange,
}: PaginationProps) {
  const isFirstPage = page === 1;
  const isLastPage = page === total;

  // 페이지가 5개 이상일 때 화살표 표시
  const showArrows = total >= 5;

  // 표시할 페이지 번호들 계산
  const getVisiblePages = () => {
    if (total <= 5) {
      return Array.from({ length: total }, (_, i) => i + 1);
    }

    // 5개 이상일 때 현재 페이지 기준으로 앞뒤 2개씩 표시
    let start = Math.max(1, page - 2);
    let end = Math.min(total, start + 4);

    // 끝에서 부족한 만큼 앞쪽으로 조정
    if (end - start < 4) {
      start = Math.max(1, end - 4);
    }

    return Array.from({ length: end - start + 1 }, (_, i) => start + i);
  };

  const visiblePages = getVisiblePages();

  return (
    <div className="flex items-center justify-center gap-2">
      {showArrows && (
        <button
          disabled={isFirstPage}
          onClick={() => !isFirstPage && onPageChange(page - 1)}
          className={twMerge(
            "flex h-8 w-8 items-center justify-center rounded border border-gray-300 text-gray-400",
            !isFirstPage && "cursor-pointer text-gray-700 hover:bg-gray-50"
          )}
        >
          <ArrowLeftIcon className="h-4 w-4" />
        </button>
      )}

      {visiblePages.map((pageNum) => (
        <button
          key={pageNum}
          onClick={() => onPageChange(pageNum)}
          className={twMerge(
            "flex h-8 w-8 items-center justify-center rounded border text-sm font-medium transition-colors",
            pageNum === page
              ? "border-blue-500 bg-blue-500 text-white"
              : "border-gray-300 text-gray-700 hover:bg-gray-50"
          )}
        >
          {pageNum}
        </button>
      ))}

      {showArrows && (
        <button
          disabled={isLastPage}
          onClick={() => !isLastPage && onPageChange(page + 1)}
          className={twMerge(
            "flex h-8 w-8 items-center justify-center rounded border border-gray-300 text-gray-400",
            !isLastPage && "cursor-pointer text-gray-700 hover:bg-gray-50"
          )}
        >
          <ArrowRightIcon className="h-4 w-4" />
        </button>
      )}
    </div>
  );
}

const OmniTable = {
  Wrapper,
  Header,
  Body,
  Row,
  Column,
  RowManageButtons,
  Pagination,
};

export default OmniTable;